import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const OffMarketModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    notes: ''
  });
  const [sendInitialEmail, setSendInitialEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      alert('Please fill in at least name and email.');
      return;
    }

    setSubmitting(true);
    try {
      // Create the off-market searcher document
      const offMarketData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null,
        notes: formData.notes || null,
        createdAt: serverTimestamp(),
        initialEmailSentAt: sendInitialEmail ? serverTimestamp() : null,
        lastEmailSentAt: sendInitialEmail ? serverTimestamp() : null,
        emailCount: sendInitialEmail ? 1 : 0,
        responded: false,
        respondedAt: null,
        invoiceSent: false,
        invoiceSentAt: null,
        status: sendInitialEmail ? 'awaiting_response' : 'pending_email'
      };

      const docRef = await addDoc(collection(db, 'offMarketSearchers'), offMarketData);

      // Send initial email if checkbox is checked
      if (sendInitialEmail) {
        const firstName = formData.name.split(' ')[0];
        const emailData = {
          to: formData.email,
          from: 'Isak @ Lended <isak@lended.ai>',
          replyTo: 'isak@lended.ai',
          subject: 'Off-Market Deal Sourcing - Next Steps',
          message: {
            subject: 'Off-Market Deal Sourcing - Next Steps',
            html: `
              <p>Hi ${firstName},</p>
              <p>Thank you for your interest in our off-market deal sourcing service. Here are the next steps to get started:</p>
              <ol>
                <li>[Placeholder - User will provide specific steps]</li>
                <li>[Placeholder]</li>
                <li>[Placeholder]</li>
              </ol>
              <p>Please let me know if you have any questions or if there's anything specific you're looking for.</p>
              <p>Best regards,<br>Isak<br>Lended</p>
            `
          }
        };

        await addDoc(collection(db, 'email'), emailData);
      }

      // Reset form
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        notes: ''
      });
      setSendInitialEmail(true);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
      alert('Off-market searcher added successfully!');
    } catch (error) {
      console.error('Error creating off-market searcher:', error);
      alert('Error creating off-market searcher. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Add Off-Market Searcher</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Full name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="email@example.com"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Any additional notes..."
            />
          </div>

          {/* Send Initial Email Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sendInitialEmail"
              checked={sendInitialEmail}
              onChange={(e) => setSendInitialEmail(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="sendInitialEmail" className="ml-2 block text-sm text-gray-700">
              Send initial off-market email
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Adding...' : 'Add Searcher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OffMarketModal;
